import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Dashboard } from "@/pages/Dashboard";
import { Receipts } from "@/pages/Receipts";
import Inbox from "@/pages/Inbox";
import Warranties from "@/pages/Warranties";
import Contracts from "@/pages/Contracts";
import Documents from "@/pages/Documents";
import Inventory from "@/pages/Inventory";
import MobileReceiptScan from "@/pages/MobileReceiptScan";
import AuditLog from "@/pages/AuditLog";
import { Invoices } from "@/pages/Invoices";
import Quotes from "@/pages/Quotes";
import { RecurringInvoices } from "@/pages/RecurringInvoices";
import { Settings } from "@/pages/Settings";
import { Journal } from "@/pages/Journal";
import AutoBookings from "@/pages/AutoBookings";
import Holding from "@/pages/Holding";
import { Reports } from "@/pages/Reports";
import PaymentFeesReport from "@/pages/PaymentFeesReport";
import SubscriptionsOverview from "@/pages/SubscriptionsOverview";
import SimpleLedger from "@/pages/SimpleLedger";
import Orders from "@/pages/Orders";
import { BankReconciliation } from "@/pages/BankReconciliation";
import { Login } from "@/pages/Login";
import { InviteRedeem } from "@/pages/InviteRedeem";
import TaxSharePage from "@/pages/TaxSharePage";
import { Register } from "@/pages/Register";
import { ForgotPassword } from "@/pages/ForgotPassword";
import { ResetPassword } from "@/pages/ResetPassword";
import { Suppliers } from "@/pages/Suppliers";
import { Customers } from "@/pages/Customers";
import LsvMandates from "@/pages/LsvMandates";
import { Bills } from "@/pages/Bills";
import { Cashbook } from "@/pages/Cashbook";
import { Payroll } from "@/pages/Payroll";
import WorkTime from "@/pages/WorkTime";
import MyTime from "@/pages/MyTime";
import EmployeeOnboarding from "@/pages/EmployeeOnboarding";
import { Assets } from "@/pages/Assets";
import { Products } from "@/pages/Products";
import { Employees } from "@/pages/Employees";
import { PayrollConfigPage } from "@/pages/PayrollConfig";
import { YearEnd } from "@/pages/YearEnd";
import OpeningBalance from "@/pages/OpeningBalance";
import { PayrollReport } from "@/pages/PayrollReport";
import { Payslip } from "@/pages/Payslip";
import { SalaryCertificate } from "@/pages/SalaryCertificate";
import { Dunning } from "@/pages/Dunning";
import { NewCompany } from "@/pages/NewCompany";
import { Portfolio } from "@/pages/Portfolio";
import { AdvisorCockpit } from "@/pages/AdvisorCockpit";
import TrusteeCockpit from "@/pages/TrusteeCockpit";
import { Projects } from "@/pages/Projects";
import { TimeEntries } from "@/pages/TimeEntries";
import { Developers } from "@/pages/Developers";
import { Partner } from "@/pages/Partner";
import Privacy from "@/pages/Privacy";
import DataDeletion from "@/pages/DataDeletion";
import Terms from "@/pages/Terms";
import Account from "@/pages/Account";
import SuperAdmin from "@/pages/SuperAdmin";
import Billing from "@/pages/Billing";
import Shopify from "@/pages/Shopify";
import Woocommerce from "@/pages/Woocommerce";
import Ebill from "@/pages/Ebill";
import Ebics from "@/pages/Ebics";
import Onboarding from "@/pages/Onboarding";
import Compliance from "@/pages/Compliance";
import Loans from "@/pages/Loans";
import LoanDetail from "@/pages/LoanDetail";
import Mftpf from "@/pages/Mftpf";
import Impressum from "@/pages/Impressum";
import Plattformen from "@/pages/Plattformen";
import { MainLayout } from "@/layout/MainLayout";
import { CompanyProvider } from "@/context/CompanyContext";
import { UploadProvider } from "@/context/UploadContext";
import { useAuth } from "@/context/AuthContext";


const ProtectedRoute = ({ children }) => {
  const { me, email } = useAuth();

  if (!email) {
    return <Navigate to="/login" replace />;
  }

  // Wenn /auth/me bereits geladen UND Profil unvollständig (Code-Login Erstanmelder)
  // → zurück zum Login, damit der Profil-Step ausgefüllt wird.
  if (me && me.profile_completed === false && !me.is_super_admin) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <BrowserRouter>
      <CompanyProvider>
        <UploadProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/treuhand" element={<Login variant="trustee" />} />
          <Route path="/invite/:token" element={<InviteRedeem />} />
          <Route path="/share/tax/:token" element={<TaxSharePage />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/partner" element={<Partner />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/plattformen" element={<Plattformen />} />
          <Route
            path="/scan"
            element={
              <ProtectedRoute>
                <MobileReceiptScan />
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-log"
            element={
              <ProtectedRoute>
                <AuditLog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-time"
            element={
              <ProtectedRoute>
                <MyTime />
              </ProtectedRoute>
            }
          />
          <Route path="/onboarding/:token" element={<EmployeeOnboarding />} />
          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/cockpit" element={<AdvisorCockpit />} />
            <Route path="/treuhand/cockpit" element={<TrusteeCockpit />} />
            <Route path="/new-company" element={<NewCompany />} />
            <Route path="/receipts" element={<Receipts />} />
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/warranties" element={<Warranties />} />
            <Route path="/contracts" element={<Contracts />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/quotes" element={<Quotes />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/recurring-invoices" element={<RecurringInvoices />} />
            <Route path="/dunning" element={<Dunning />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/auto-bookings" element={<AutoBookings />} />
            <Route path="/holding" element={<Holding />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/payment-fees" element={<PaymentFeesReport />} />
            <Route path="/subscriptions" element={<SubscriptionsOverview />} />
            <Route path="/simple-ledger" element={<SimpleLedger />} />
            <Route path="/bank-reconciliation" element={<BankReconciliation />} />
            <Route path="/bank" element={<Navigate to="/bank-reconciliation" replace />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/lsv-mandates" element={<LsvMandates />} />
            <Route path="/suppliers" element={<Suppliers />} />
            <Route path="/bills" element={<Bills />} />
            <Route path="/loans" element={<Loans />} />
            <Route path="/loans/:loanId" element={<LoanDetail />} />
            <Route path="/integrations/mftpf" element={<Mftpf />} />
            <Route path="/cashbook" element={<Cashbook />} />
            <Route path="/payroll" element={<Payroll />} />
            <Route path="/work-time" element={<WorkTime />} />
            <Route path="/assets" element={<Assets />} />
            <Route path="/products" element={<Products />} />
            <Route path="/employees" element={<Employees />} />
            <Route path="/payroll-config" element={<PayrollConfigPage />} />
            <Route path="/payroll-report" element={<PayrollReport />} />
            <Route path="/payslip/:runId" element={<Payslip />} />
            <Route path="/salary-certificate/:employeeId" element={<SalaryCertificate />} />
            <Route path="/year-end" element={<YearEnd />} />
            <Route path="/opening-balance" element={<OpeningBalance />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/time-entries" element={<TimeEntries />} />
            <Route path="/developers" element={<Developers />} />
            <Route path="/account" element={<Account />} />
            <Route path="/super-admin" element={<SuperAdmin />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/integrations/shopify" element={<Shopify />} />
            <Route path="/integrations/woocommerce" element={<Woocommerce />} />
            <Route path="/integrations/ebill" element={<Ebill />} />
            <Route path="/integrations/ebics" element={<Ebics />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/compliance" element={<Compliance />} />
          </Route>
        </Routes>
        </UploadProvider>
      </CompanyProvider>
    </BrowserRouter>
  );
}

export default App;
